import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all locale files
import en from './locales/en.json';
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';
import es from './locales/es.json';
import ar from './locales/ar.json';
import fr from './locales/fr.json';
import ru from './locales/ru.json';
import pt from './locales/pt.json';
import id from './locales/id.json';
import fa from './locales/fa.json';
import de from './locales/de.json';
import ja from './locales/ja.json';
import tr from './locales/tr.json';
import it from './locales/it.json';
import vi from './locales/vi.json';
import ko from './locales/ko.json';
import pl from './locales/pl.json';
import nl from './locales/nl.json';
import uk from './locales/uk.json';
import he from './locales/he.json';
import cs from './locales/cs.json';
import da from './locales/da.json';
import fi from './locales/fi.json';
import el from './locales/el.json';
import ms from './locales/ms.json';
import ro from './locales/ro.json';
import sv from './locales/sv.json';
import th from './locales/th.json';
import hu from './locales/hu.json';
import sr from './locales/sr.json';
import nb from './locales/nb.json';
import ca from './locales/ca.json';

// RTL languages
export const RTL_LANGUAGES = ['ar', 'he', 'fa'];

// Language configuration with native names
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'zh-CN', name: 'Simplified Chinese', nativeName: '简体中文' },
  { code: 'zh-TW', name: 'Traditional Chinese', nativeName: '繁體中文' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', rtl: true },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська' },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', rtl: true },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar' },
  { code: 'sr', name: 'Serbian', nativeName: 'Srpski' },
  { code: 'nb', name: 'Norwegian Bokmål', nativeName: 'Norsk Bokmål' },
  { code: 'ca', name: 'Catalan', nativeName: 'Català' },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code'];

const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export const normalizeSupportedLanguage = (language?: string | null): SupportedLanguage => {
  const raw = (language || '').trim();
  if (!raw) {
    return DEFAULT_LANGUAGE;
  }

  const exactMatch = SUPPORTED_LANGUAGES.find(
    (supported) => supported.code.toLowerCase() === raw.toLowerCase()
  );
  if (exactMatch) {
    return exactMatch.code;
  }

  const baseLanguage = raw.split('-')[0]?.toLowerCase();
  const baseMatch = SUPPORTED_LANGUAGES.find(
    (supported) => supported.code.toLowerCase() === baseLanguage
  );

  return baseMatch?.code || DEFAULT_LANGUAGE;
};

const resources = {
  en: { translation: en },
  'zh-CN': { translation: zhCN },
  'zh-TW': { translation: zhTW },
  es: { translation: es },
  ar: { translation: ar },
  fr: { translation: fr },
  ru: { translation: ru },
  pt: { translation: pt },
  id: { translation: id },
  fa: { translation: fa },
  de: { translation: de },
  ja: { translation: ja },
  tr: { translation: tr },
  it: { translation: it },
  vi: { translation: vi },
  ko: { translation: ko },
  pl: { translation: pl },
  nl: { translation: nl },
  uk: { translation: uk },
  he: { translation: he },
  cs: { translation: cs },
  da: { translation: da },
  fi: { translation: fi },
  el: { translation: el },
  ms: { translation: ms },
  ro: { translation: ro },
  sv: { translation: sv },
  th: { translation: th },
  hu: { translation: hu },
  sr: { translation: sr },
  nb: { translation: nb },
  ca: { translation: ca },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false,
    },

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'omniscribe-language',
    },

    react: {
      useSuspense: true,
    },
  });

// Helper to check if current language is RTL
export const isRTL = (lang?: string): boolean => {
  const currentLang = normalizeSupportedLanguage(lang || i18n.resolvedLanguage || i18n.language);
  return RTL_LANGUAGES.includes(currentLang);
};

// Helper to get direction
export const getDirection = (lang?: string): 'ltr' | 'rtl' => {
  return isRTL(lang) ? 'rtl' : 'ltr';
};

export default i18n;
